<script lang="ts" setup>
import { useFormContext } from "vee-validate";
import { computed, type HTMLAttributes } from "vue";
import { VList, VMenu } from "vuetify/components";

import { shouldDisableSubmit } from "#utilities/form";
import { appIcons } from "#utilities/icons";
import FormSubmit, { type FormSubmitProps } from "./FormSubmit.vue";

interface FormSubmitMenuProps extends FormSubmitProps {
  class?: HTMLAttributes["class"];
  color?: string;
  disableMenu?: boolean;
  listProps?: VList["$props"];
  menuProps?: VMenu["$props"];
}

const props = withDefaults(defineProps<FormSubmitMenuProps>(), {
  class: undefined,
  color: "primary",
  listProps: undefined,
  menuProps: undefined,
});

defineEmits<{
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  (e: "click"): void;
}>();

const formContext = useFormContext();

const submitDisabled = computed(() => shouldDisableSubmit(formContext) || props.disabled);
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
const menuDisabled = computed(() => submitDisabled.value || props.disableMenu);
</script>

<template>
  <VBtnGroup :class="props.class" :color="color" density="compact">
    <FormSubmit :disabled="submitDisabled" :loading="loading" @click="$emit('click')">
      <slot name="default" />
    </FormSubmit>

    <template v-if="$slots['menu']">
      <VDivider color="white" vertical />
      <VMenu
        v-bind="menuProps"
        :disabled="menuDisabled"
        :location="menuProps?.location ?? 'bottom end'"
        :offset="menuProps?.offset ?? 4"
      >
        <template #activator="{ props: activatorProps }">
          <VBtn
            class="pr-1"
            :disabled="menuDisabled"
            :icon="appIcons.mdiMenuDown"
            v-bind="activatorProps"
          />
        </template>
        <VList v-bind="listProps" class="py-1" :density="listProps?.density ?? 'compact'">
          <slot name="menu" v-bind="{ disabled, menuDisabled }" />
        </VList>
      </VMenu>
    </template>
  </VBtnGroup>
</template>
